import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { usePagePermission } from '../../hooks/usePagePermission'
import { FUTURES_ALL_ROWS_PAGE_SIZE, useGetFuturesReportQuery } from '../../store/api/futuresControlApi'
import { WIDE_SCROLL_WRAPPER } from '../../theme/responsive'
import FuturesChangeMonthDialog from './FuturesChangeMonthDialog'

type ReportRow = Record<string, unknown>

const BANKS = [
  { key: 'Vietinbank', label: 'VietinBank' },
  { key: 'BIDV', label: 'BIDV' },
  { key: 'MBbank', label: 'MBBank' },
] as const

const headerBarSx = {
  mb: 0.75,
  px: { xs: 0.75, sm: 1 },
  py: { xs: 0.45, sm: 0.5 },
  borderRadius: 2,
  border: '1px solid rgba(11, 54, 51, 0.1)',
  background: 'linear-gradient(135deg, #ffffff 0%, #f6faf9 55%, #fff9f4 100%)',
  boxShadow: 'var(--app-shadow-soft, 0 10px 28px -24px rgba(11, 54, 51, 0.45))',
}

const headerTitleSx = {
  color: 'primary.dark',
  fontWeight: 900,
  letterSpacing: '-0.025em',
  lineHeight: 1.15,
  textTransform: 'uppercase' as const,
  fontSize: { xs: '0.82rem', sm: '0.9rem' },
  whiteSpace: 'nowrap' as const,
}

const moduleChipSx = {
  height: 18,
  borderRadius: 999,
  bgcolor: 'rgba(244, 223, 156, 0.28)',
  color: 'secondary.dark',
  border: '1px solid rgba(218, 131, 60, 0.28)',
  fontWeight: 900,
  fontSize: '0.6rem',
  letterSpacing: 0.5,
}

const commandBarSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 0.75,
  flexWrap: 'wrap' as const,
  px: 1,
  py: 0.5,
  mb: 0.75,
  borderRadius: 2,
  border: '1px solid var(--app-border-subtle, rgba(11, 54, 51, 0.12))',
  borderBottom: '2px solid rgba(244, 223, 156, 0.55)',
  background: 'linear-gradient(180deg, #ffffff 0%, #fffbf7 100%)',
  boxShadow: '0 1px 2px rgba(11, 54, 51, 0.04)',
}

const commandBtnSx = {
  minWidth: 0,
  px: 0.75,
  py: 0.15,
  minHeight: 22,
  fontSize: '0.66rem',
  lineHeight: 1.1,
  textTransform: 'none' as const,
  color: 'primary.main',
  borderColor: 'primary.main',
}

const reportShellSx = {
  flex: 1,
  minHeight: 0,
  /** Một lớp scroll duy nhất cho toàn Summary (ngang + dọc). */
  overflow: 'auto',
  border: '1px solid var(--app-border-subtle, rgba(11, 54, 51, 0.12))',
  borderRadius: '14px',
  bgcolor: 'var(--app-card-bg, #ffffff)',
  p: 1,
  boxShadow: 'var(--app-shadow-soft, 0 10px 30px -26px rgba(11, 54, 51, 0.42))',
}

const reportPageSx = {
  width: '100%',
  minWidth: { xs: 1460, md: 'auto' },
  minHeight: 0,
  overflow: 'visible',
  background: 'linear-gradient(180deg, #ffffff 0%, #fffdf8 100%)',
  border: '1px solid rgba(11, 54, 51, 0.06)',
  borderRadius: '12px',
  p: 1.25,
  fontSize: '0.72rem',
  color: '#000000',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.92)',
}

/** Tier 1 — tiêu đề sub-report (Cash, PnL, ORDERS, Hedging, Ngày, PHYSICAL HEDGES…): cream → gold nhạt, viền copper. */
const sectionTitleSx = {
  height: 20,
  px: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.66rem',
  fontWeight: 900,
  letterSpacing: '0.07em',
  color: '#000000',
  background: 'linear-gradient(180deg, #ffffff 0%, #fffbf4 64%, rgba(244, 223, 156, 0.36) 100%)',
  border: '1px solid rgba(218, 131, 60, 0.16)',
  borderBottom: '2px solid rgba(169, 94, 37, 0.22)',
  borderTopLeftRadius: '6px',
  borderTopRightRadius: '6px',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.9)',
  textTransform: 'uppercase' as const,
}

/** Tier 2 — băng toàn chiều ngang (TOTAL POSITIONS): gold đậm hơn tier 1, chữ forest (không nền teal). */
const darkTitleSx = {
  height: 22,
  px: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.7rem',
  fontWeight: 900,
  letterSpacing: '0.08em',
  color: '#000000',
  background: 'linear-gradient(180deg, #fff8e6 0%, #f4df9c 52%, #e4c36c 100%)',
  border: '1px solid rgba(169, 94, 37, 0.34)',
  borderBottom: '2px solid rgba(11, 54, 51, 0.24)',
  borderTopLeftRadius: '6px',
  borderTopRightRadius: '6px',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.72)',
  textTransform: 'uppercase' as const,
}

/** Tier 1b — cột nhấn copper (PROPS): stripe trái, peach nhạt. */
const propTitleSx = {
  ...sectionTitleSx,
  color: '#000000',
  background: 'linear-gradient(180deg, #ffffff 0%, #fff4e8 76%, rgba(251, 212, 168, 0.5) 100%)',
  border: '1px solid rgba(218, 131, 60, 0.22)',
  borderBottom: '2px solid rgba(169, 94, 37, 0.28)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.9), inset 3px 0 0 rgba(218, 131, 60, 0.45)',
}

const totalRowSx = {
  fontWeight: 800,
  color: '#000000',
}

const totalRowBodySx = {
  ...totalRowSx,
  backgroundColor: 'rgba(244, 223, 156, 0.16)',
  borderTop: '1px solid rgba(169, 94, 37, 0.22)',
}

const totalRowFooterSx = {
  ...totalRowSx,
  background: 'linear-gradient(180deg, #fffefb 0%, #fbf0d4 100%)',
  borderTop: '2px solid rgba(11, 54, 51, 0.2)',
}

const tableSx = {
  tableLayout: 'auto',
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
  '& th, & td': {
    height: 20,
    px: 0.5,
    py: 0.15,
    border: '1px solid rgba(11, 54, 51, 0.07)',
    fontSize: '0.65rem',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: '#000000',
  },
  '& td': {
    textTransform: 'none',
  },
  '& th': {
    fontSize: '0.66rem',
    fontWeight: 800,
    letterSpacing: '0.04em',
    textAlign: 'center',
    color: '#000000',
    backgroundColor: '#fffdfb',
    backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #fff9f4 55%, rgba(244, 223, 156, 0.36) 100%)',
    borderBottom: '1px solid rgba(169, 94, 37, 0.2)',
  },
  '& td.num': {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    fontFeatureSettings: '"tnum"',
    textOverflow: 'clip',
    minWidth: 52,
    px: 0.45,
  },
  '& td.num-center': {
    textAlign: 'center',
    fontVariantNumeric: 'tabular-nums',
    fontFeatureSettings: '"tnum"',
    textOverflow: 'clip',
    minWidth: 48,
    px: 0.4,
  },
  '& td.emphasis-cell': {
    fontWeight: 800,
  },
  '& tbody tr td': {
    bgcolor: '#ffffff',
  },
  '& tr.parent td': {
    fontWeight: 800,
    color: '#000000',
    bgcolor: '#ffffff',
  },
  '& tr.emphasis td': {
    fontWeight: 800,
    color: '#000000',
  },
  '& td.indent-cell': {
    pl: 1.6,
  },
  '& tbody tr.total td': {
    ...totalRowBodySx,
  },
  '& tfoot tr.total td': {
    ...totalRowFooterSx,
  },
}

/** Cuộn trong từng sub-report (ORDERS / TOTAL POSITIONS…); sticky thead + total footer. */
const scrollBoxSx = (maxHeight: number | string, allowHorizontal = false) => ({
  maxHeight,
  overflowY: 'auto' as const,
  overflowX: allowHorizontal ? ('auto' as const) : ('hidden' as const),
  border: '1px solid rgba(11, 54, 51, 0.08)',
  borderTop: 'none',
  borderRadius: '0 0 6px 6px',
  '& thead th': {
    position: 'sticky' as const,
    top: 0,
    zIndex: 3,
    backgroundColor: '#fffdfb',
    backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #fff9f4 55%, rgba(244, 223, 156, 0.36) 100%)',
    boxShadow: '0 1px 0 rgba(169, 94, 37, 0.18), inset 0 -1px 0 rgba(244, 223, 156, 0.35)',
  },
  '& tfoot tr.total td': {
    position: 'sticky' as const,
    bottom: 0,
    zIndex: 2,
    ...totalRowFooterSx,
    boxShadow: '0 -1px 0 rgba(169, 94, 37, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.72)',
  },
})

const toRows = (items: unknown[] | undefined): ReportRow[] => (items ?? []) as ReportRow[]

const bankEquals = (row: ReportRow, bank: string) =>
  String(row.bank ?? '').trim().toLowerCase() === bank.toLowerCase()

const displayValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'number') {
    const abs = Math.abs(value)
    const digits = abs >= 1000 ? 0 : abs >= 1 ? 2 : abs > 0 ? 4 : 0
    const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: digits }).format(abs)
    if (value === 0) return formatted
    return value < 0 ? `(${formatted})` : formatted
  }
  return String(value)
}

const displayAccessInteger = (value: unknown) => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value !== 'number') return String(value)
  if (value === 0) return '-'
  const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.abs(value))
  return value < 0 ? `(${formatted})` : formatted
}

const textValue = (row: ReportRow, ...keys: string[]) => {
  for (const key of keys) {
    const value = row[key]
    if (value !== null && value !== undefined && value !== '') return displayValue(value)
  }
  return ''
}

type ColumnDef = {
  key: string
  label: string
  numeric?: boolean
  width?: string
  minWidth?: number
  sourceKeys?: string[]
}

const compactCellOverrideSx = {
  '& th, & td': {
    height: 18,
    px: 0.25,
    py: 0.1,
    fontSize: '0.6rem',
  },
  '& th': {
    fontSize: '0.6rem',
    letterSpacing: 0,
  },
  '& td.num': {
    minWidth: 44,
    px: 0.3,
  },
  '& td.num-center': {
    minWidth: 40,
    px: 0.3,
  },
}

const isTotalRow = (row: ReportRow) =>
  String(row.fullSymbol ?? row.type ?? row.name ?? '').trim().toLowerCase() === 'total'
const isParentRow = (row: ReportRow) =>
  row.isParent === 1 || row.isParent === true || row.isParent === '1'

const normalizeRowLabel = (value: unknown) => String(value ?? '').trim().toLowerCase()

const matchesRowLabel = (row: ReportRow, labels: readonly string[]) => {
  const label = normalizeRowLabel(row.type ?? row.name ?? row.fullSymbol)
  return labels.some((item) => item.toLowerCase() === label)
}

function ReportTable({
  columns,
  rows,
  minRows = 0,
  scrollMaxHeight,
  horizontalScroll = false,
  tableMinWidth,
  density = 'normal',
  pinTotalRow = false,
  boldText = false,
  highlightParentRows = true,
  emphasisRowLabels = [],
  emphasisColumnKeys = [],
  indentNonEmphasisFirstColumn = false,
  numericFormatter,
  numericAlign = 'right',
}: {
  columns: ColumnDef[]
  rows: ReportRow[]
  minRows?: number
  scrollMaxHeight?: number | string
  horizontalScroll?: boolean
  tableMinWidth?: number | string
  density?: 'normal' | 'compact'
  pinTotalRow?: boolean
  boldText?: boolean
  highlightParentRows?: boolean
  emphasisRowLabels?: string[]
  emphasisColumnKeys?: string[]
  indentNonEmphasisFirstColumn?: boolean
  numericFormatter?: (value: unknown) => string
  numericAlign?: 'right' | 'center'
}) {
  const bodyRows = pinTotalRow ? rows.filter((r) => !isTotalRow(r)) : rows
  const footerRows = pinTotalRow ? rows.filter(isTotalRow) : []

  const paddedRows = useMemo(() => {
    if (bodyRows.length >= minRows) return bodyRows
    return [...bodyRows, ...Array.from({ length: minRows - bodyRows.length }, () => ({} as ReportRow))]
  }, [minRows, bodyRows])

  const tableInnerSx = {
    ...tableSx,
    ...(density === 'compact' ? compactCellOverrideSx : {}),
    ...(tableMinWidth ? { minWidth: tableMinWidth } : {}),
    ...(boldText ? { '& th, & td': { ...tableSx['& th, & td'], fontWeight: 700 } } : {}),
  }
  const emphasisColumnSet = new Set(emphasisColumnKeys)

  const renderCell = (row: ReportRow, col: ColumnDef) => {
    const keys = col.sourceKeys ?? [col.key]
    for (const key of keys) {
      const value = row[key]
      if (value !== null && value !== undefined && value !== '') {
        return col.numeric && numericFormatter ? numericFormatter(value) : displayValue(value)
      }
    }
    return ''
  }

  const table = (
    <Table size="small" sx={tableInnerSx}>
      <TableHead>
        <TableRow>
          {columns.map((col) => (
            <TableCell
              key={col.key}
              sx={{
                ...(col.width ? { width: col.width } : {}),
                ...(col.minWidth ? { minWidth: col.minWidth } : {}),
              }}
            >
              {col.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {paddedRows.map((row, index) => {
          const totalInline = !pinTotalRow && isTotalRow(row)
          const parent = highlightParentRows && isParentRow(row)
          const emphasis = matchesRowLabel(row, emphasisRowLabels)
          const className = [totalInline ? 'total' : '', parent ? 'parent' : '', emphasis ? 'emphasis' : '']
            .filter(Boolean)
            .join(' ') || undefined
          return (
            <TableRow
              key={`${String(row.fullSymbol ?? row.type ?? row.name ?? 'blank')}-${index}`}
              className={className}
            >
              {columns.map((col, colIndex) => (
                <TableCell
                  key={col.key}
                  className={[
                    col.numeric ? (numericAlign === 'center' ? 'num-center' : 'num') : '',
                    emphasisColumnSet.has(col.key) ? 'emphasis-cell' : '',
                    indentNonEmphasisFirstColumn && colIndex === 0 && !totalInline && !parent && !emphasis
                      ? 'indent-cell'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined}
                >
                  {renderCell(row, col)}
                </TableCell>
              ))}
            </TableRow>
          )
        })}
      </TableBody>
      {footerRows.length > 0 ? (
        <TableFooter>
          {footerRows.map((row, i) => (
            <TableRow key={`total-${i}`} className="total">
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  className={col.numeric ? (numericAlign === 'center' ? 'num-center' : 'num') : undefined}
                >
                  {renderCell(row, col)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableFooter>
      ) : null}
    </Table>
  )

  if (scrollMaxHeight) {
    return <Box sx={scrollBoxSx(scrollMaxHeight, horizontalScroll)}>{table}</Box>
  }
  return table
}

function Section({
  title,
  rows,
  columns,
  minRows,
  titleSx = sectionTitleSx,
  scrollMaxHeight,
  pinTotalRow,
  boldText,
  highlightParentRows,
  emphasisRowLabels,
  emphasisColumnKeys,
  indentNonEmphasisFirstColumn,
  numericFormatter,
  numericAlign,
}: {
  title: string
  rows: ReportRow[]
  columns: ColumnDef[]
  minRows?: number
  titleSx?: object
  scrollMaxHeight?: number | string
  pinTotalRow?: boolean
  boldText?: boolean
  highlightParentRows?: boolean
  emphasisRowLabels?: string[]
  emphasisColumnKeys?: string[]
  indentNonEmphasisFirstColumn?: boolean
  numericFormatter?: (value: unknown) => string
  numericAlign?: 'right' | 'center'
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={titleSx}>{title}</Box>
      <ReportTable
        columns={columns}
        rows={rows}
        minRows={minRows}
        scrollMaxHeight={scrollMaxHeight}
        pinTotalRow={pinTotalRow}
        boldText={boldText}
        highlightParentRows={highlightParentRows}
        emphasisRowLabels={emphasisRowLabels}
        emphasisColumnKeys={emphasisColumnKeys}
        indentNonEmphasisFirstColumn={indentNonEmphasisFirstColumn}
        numericFormatter={numericFormatter}
        numericAlign={numericAlign}
      />
    </Box>
  )
}

export default function FuturesSummaryReportPage() {
  const { canEdit } = usePagePermission()
  const [changeMonthOpen, setChangeMonthOpen] = useState(false)

  const cashBalance = useGetFuturesReportQuery({ kind: 'cash-balance', page: 1, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })
  const pnl = useGetFuturesReportQuery({ kind: 'pnl', page: 1, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })
  const reconciliation = useGetFuturesReportQuery({ kind: 'reconciliation', page: 1, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })
  const orders = useGetFuturesReportQuery({ kind: 'summary', page: 1, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })
  const position = useGetFuturesReportQuery({ kind: 'position', page: 1, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })

  const loading =
    cashBalance.isLoading || pnl.isLoading || reconciliation.isLoading || orders.isLoading || position.isLoading
  const fetching =
    cashBalance.isFetching || pnl.isFetching || reconciliation.isFetching || orders.isFetching || position.isFetching
  const hasError = Boolean(cashBalance.error || pnl.error || reconciliation.error || orders.error || position.error)

  const cashRows = toRows(cashBalance.data?.items)
  const pnlRows = toRows(pnl.data?.items)
  const reconciliationRows = toRows(reconciliation.data?.items)
  const orderRows = toRows(orders.data?.items)
  const positionRows = toRows(position.data?.items)

  const cashColumns: ColumnDef[] = [
    { key: 'name', label: 'Type' },
    { key: 'value', label: 'Value', numeric: true },
  ]
  const pnlColumns: ColumnDef[] = [
    { key: 'type', label: 'Type' },
    {
      key: 'openPositionLot',
      label: 'Open Lot',
      numeric: true,
      sourceKeys: ['openPositionLot', 'openPositionLotRaw'],
    },
    {
      key: 'm2mUsd',
      label: 'M2M USD',
      numeric: true,
      sourceKeys: ['m2mUsd', 'm2mUsdRaw'],
    },
  ]
  const reconciliationColumns: ColumnDef[] = [
    { key: 'name', label: 'Type' },
    { key: 'futureBook', label: 'Future Book', numeric: true },
    { key: 'bankStatement', label: 'Bank Statement', numeric: true },
    { key: 'var', label: 'Var', numeric: true },
  ]
  const orderColumns: ColumnDef[] = [
    { key: 'fullSymbol', label: 'Full Symbol' },
    { key: 'totalBought', label: 'Bought', numeric: true },
    { key: 'totalSold', label: 'Sold', numeric: true },
    { key: 'openPosition', label: 'Open', numeric: true },
  ]
  const positionColumns: ColumnDef[] = [
    { key: 'fullSymbol', label: 'Month' },
    { key: 'longHedge', label: 'Long Hedge', numeric: true },
    { key: 'shortHedge', label: 'Short Hedge', numeric: true },
    { key: 'reBalanceHedge', label: 'Re-balance Hedge', numeric: true },
    { key: 'spreads', label: 'Spreads', numeric: true },
    { key: 'ptbfPurchase', label: 'PTBF Purchase', numeric: true },
    { key: 'ptbfSale', label: 'PTBF Sale', numeric: true },
    { key: 'ptbfSaleSeller', label: 'Seller Call', numeric: true },
    { key: 'ptbfSaleBuyer', label: 'Buyer Call', numeric: true },
    { key: 'position', label: 'Position', numeric: true },
    { key: 'preHedge', label: 'Pre-hedge', numeric: true },
    { key: 'postHedge', label: 'Post-hedge', numeric: true },
    { key: 'arbPosition', label: 'Arb-position', numeric: true },
    { key: 'props', label: 'Props', numeric: true },
    { key: 'netTotalPositions', label: 'Net Total Position', numeric: true },
    { key: 'netFutures', label: 'Net Futures', numeric: true },
    { key: 'realizedPnl', label: 'Realized PnL', numeric: true },
    { key: 'm2mPnl', label: 'M2M PnL', numeric: true },
    { key: 'checkNetFutures', label: 'Check Net Futures', numeric: true },
  ]

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* <Box sx={headerBarSx}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={{ xs: 0.5, lg: 0.75 }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minHeight: 28 }}>
            <Typography variant="h6" sx={headerTitleSx}>
              Futures Summary
            </Typography>
            <Chip size="small" label="Futures" sx={moduleChipSx} />
            {fetching && !loading ? (
              <CircularProgress size={12} sx={{ ml: 0.5, color: 'primary.main' }} />
            ) : null}
          </Stack>
        </Stack>
      </Box> */}

      <Box sx={commandBarSx}>
        <Button
          size="small"
          variant="outlined"
          sx={commandBtnSx}
          disabled={!canEdit}
          onClick={() => setChangeMonthOpen(true)}
        >
          Change Futures Month
        </Button>
      </Box>

      <FuturesChangeMonthDialog open={changeMonthOpen} onClose={() => setChangeMonthOpen(false)} canEdit={canEdit} />

      <Box sx={reportShellSx}>
        {hasError ? (
          <Alert
            severity="error"
            sx={{
              mb: 1,
              borderRadius: 2,
              fontSize: '0.72rem',
              py: 0.5,
            }}
          >
            Could not load part of the Summary data. Please try again later.
          </Alert>
        ) : null}
        {loading ? (
          <Box sx={{ minHeight: 360, display: 'grid', placeItems: 'center' }}>
            <CircularProgress size={24} sx={{ color: 'primary.main' }} />
          </Box>
        ) : (
          <Box sx={WIDE_SCROLL_WRAPPER}>
          <Box sx={reportPageSx}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                gridAutoFlow: 'row dense',
                columnGap: '20px',
                rowGap: '6px',
              }}
            >
              {BANKS.map((bank) => (
                <Section
                  key={`cash-${bank.key}`}
                  title={`${bank.label} Cash Balance`}
                  rows={cashRows.filter((row) => bankEquals(row, bank.key))}
                  columns={cashColumns}
                  minRows={6}
                  emphasisColumnKeys={['value']}
                />
              ))}
              <Box sx={{ minWidth: 0, gridRow: 'span 2' }}>
                <Box sx={sectionTitleSx}>Futures PnL</Box>
                <ReportTable
                  columns={pnlColumns}
                  rows={pnlRows}
                  minRows={12}
                  pinTotalRow
                  highlightParentRows={false}
                  emphasisColumnKeys={['openPositionLot', 'm2mUsd']}
                  emphasisRowLabels={[
                    'All hedges',
                    'Pre-post hedge',
                    'Futures',
                    'Arb-Position',
                    'Arb-Loic',
                    'Daniel prop',
                    'Special Ops 2526',
                    'Spread-Loic',
                    'Vinh Prop',
                  ]}
                  indentNonEmphasisFirstColumn
                />
              </Box>

              {BANKS.map((bank) => (
                <Section
                  key={`rec-${bank.key}`}
                  title={`Reconcillation with ${bank.label}`}
                  rows={reconciliationRows.filter((row) => bankEquals(row, bank.key))}
                  columns={reconciliationColumns}
                  pinTotalRow
                  emphasisColumnKeys={['futureBook', 'bankStatement', 'var']}
                />
              ))}
            </Box>

            <Box
              sx={{
                mt: 1.5,
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                columnGap: '20px',
              }}
            >
              {BANKS.map((bank) => (
                <Section
                  key={`orders-${bank.key}`}
                  title={`${bank.label.toUpperCase()} ORDERS`}
                  rows={orderRows.filter((row) => bankEquals(row, bank.key))}
                  columns={orderColumns}
                  minRows={10}
                  scrollMaxHeight={260}
                  pinTotalRow
                  numericFormatter={displayAccessInteger}
                  numericAlign="center"
                />
              ))}
              <Box />
            </Box>

            <Box sx={{ mt: 1.5 }}>
              <Box sx={darkTitleSx}>TOTAL POSITIONS</Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 52.6%) minmax(0, 47.4%)' }}>
                <Box sx={sectionTitleSx}>PHYSICAL HEDGES</Box>
                <Box sx={propTitleSx}>PROPS</Box>
              </Box>
              <ReportTable
                columns={positionColumns}
                rows={positionRows}
                minRows={12}
                scrollMaxHeight={340}
                density="compact"
                pinTotalRow
                numericFormatter={displayAccessInteger}
                numericAlign="center"
              />
            </Box>
          </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
